import {defineStore} from 'pinia'
import { markRaw } from 'vue'
import * as THREE from 'three'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import { useGameStore } from '@/stores/gameStore.ts'

export const useMapPlanetOwnerStore = defineStore('mapPlanetOwnerStore', {
    state: () => ({
        planetOwnerInstancedMesh: null as THREE.InstancedMesh<THREE.CircleGeometry, THREE.ShaderMaterial> | null,
        planetOwnerAvailableInstanceIds: markRaw<number[]>([]),
        planetOwnerMapCoordinateLookup: markRaw(new Map())
    }),
    actions: {
        initPlanetOwnerInstancedMesh(maxCount: number) {
            const geometry2 = new THREE.CircleGeometry(1, 32);
            const material2 = new THREE.ShaderMaterial({
                vertexShader: `
                    attribute vec3 instanceColor;
                    varying vec3 vColor;
                
                    void main() {
                      vColor = instanceColor;  // Pass the color to the fragment shader
                      vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
                      gl_Position = projectionMatrix * mvPosition;
                    }
                  `,
                                fragmentShader: `
                    varying vec3 vColor;
                
                    void main() {
                      gl_FragColor = vec4(vColor, 1.0);  // Use the passed color for the fragment
                    }
                  `,
                vertexColors: true,
                transparent: true,
                depthTest: false,
                depthWrite: false
            });

            const colors = new Float32Array(maxCount * 3);  // 3 values per instance (r, g, b)
            geometry2.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(colors, 3));

            this.planetOwnerInstancedMesh = markRaw(new THREE.InstancedMesh(geometry2, material2, maxCount));
            this.planetOwnerInstancedMesh.renderOrder = 3; // Higher renderOrder to render in front of ships and stars
            this.planetOwnerInstancedMesh.frustumCulled = false; // Disable frustum culling to prevent disappearing on large maps

            const dummy = new THREE.Object3D();

            for(let i = 0; i < maxCount; ++i) {
                dummy.position.set(-10000, -10000, -10000);  // Out of visible range
                dummy.updateMatrix();
                this.planetOwnerInstancedMesh.setMatrixAt(i, dummy.matrix);
                this.planetOwnerAvailableInstanceIds.push(i);
            }

            this.planetOwnerInstancedMesh.instanceMatrix.needsUpdate = true;

            return this.planetOwnerInstancedMesh
        },
        getColor(mapCoordinate: MapCoordinate): number {
          if(Number(mapCoordinate.type) === 2) {
            const gameStore = useGameStore();
            if(mapCoordinate.typePlayerId == gameStore.player?.id) {
                return 0x00FF00;
            }
            const playerRelationStore = usePlayerRelationStore();
            const relation = playerRelationStore.findRelationByPlayerId(mapCoordinate.typePlayerId);

              if(relation.relation == 'neutral') {
                  return 0xFFFF00;
              } else if(relation.relation == 'enemy') {
                  return 0xFF0000;
              } else if(relation.relation == 'friend') {
                  return 0xCE93D8;
              } else if(relation.relation == 'ally') {
                  return 0x00FFFF;
              } else if(relation.relation == 'alliance') {
                  return 0x0000FF;
              } else {
                return 0x909090;
              }
          }
          return 0x123456;
        },
        addPlanetOwner(x:number,y:number,color:number): number {
            if(this.planetOwnerInstancedMesh) {
                const instanceId = this.getAvailablePlanetOwnerInstanceId();
                if (instanceId !== null && instanceId !== undefined) {
                    // Reuse the same dummy Object3D instance
                    const dummy = new THREE.Object3D();  // Or reuse a global dummy object

                    // Set the new position for the instance
                    dummy.position.set(x, y, 0.1);
                    dummy.scale.set(1, 1, 1);  // Apply uniform scaling
                    dummy.updateMatrix();

                    // Apply the matrix to the instanced mesh at the specific instance ID
                    this.planetOwnerInstancedMesh.setMatrixAt(instanceId, dummy.matrix);

                    const colorAttribute = this.planetOwnerInstancedMesh.geometry.attributes.instanceColor;
                    if (colorAttribute) {
                        // Update the color buffer with the new color
                        const color2 = new THREE.Color(color);
                        colorAttribute.setXYZ(instanceId, color2.r, color2.g, color2.b);  // Assuming color is a THREE.Color object
                        colorAttribute.needsUpdate = true;
                    }

                    // Mark the instance matrix for update
                    this.planetOwnerInstancedMesh.instanceMatrix.needsUpdate = true;

                    // Store the instanceId for the mapCoordinate (so we can reverse lookup it later)
                    this.planetOwnerMapCoordinateLookup.set(instanceId, { x: x, y: y });
                    return instanceId
                }
            }
            return 0
        },
        removePlanetOwner(instanceId: number) {
            if(this.planetOwnerInstancedMesh) {
                const dummy = new THREE.Object3D();
                dummy.position.set(-10000, -10000, -10000);  // Out of visible range
                dummy.updateMatrix();
                this.planetOwnerInstancedMesh.setMatrixAt(instanceId, dummy.matrix);
                this.releasePlanetOwnerInstanceId(instanceId);
                this.planetOwnerInstancedMesh.instanceMatrix.needsUpdate = true;
            }
        },
        getAvailablePlanetOwnerInstanceId(): number {
            if(this.planetOwnerAvailableInstanceIds.length === 0) {
                throw new Error('No more available instance ids for planet owner objects');
            }
            return this.planetOwnerAvailableInstanceIds.pop()!;
        },
        releasePlanetOwnerInstanceId(instanceId: number) {
            this.planetOwnerAvailableInstanceIds.push(instanceId)
            this.planetOwnerMapCoordinateLookup.delete(instanceId);
        },
        scaleAndPositionPlanetOwner(instanceId: number, mapCoordinate: MapCoordinate, zoom: number) {
            if(this.planetOwnerInstancedMesh) {
                const dummy = new THREE.Object3D();
                let x = 0;
                let y = 0;
                let scale = 0;
                
                // Dynamic position calculation based on zoom level
                if (zoom >= zoomFactor.Solar) {
                    // For highly zoomed in levels - position circle at top-left of planet
                    x = mapCoordinate.xPos + 1;
                    y = mapCoordinate.yPos + 1;
                } else {
                    // For zoomed out levels
                    x = mapCoordinate.xPos;
                    y = mapCoordinate.yPos;
                }
                
                // Use fixed scale values at specific zoom levels for better appearance
                if (zoom === zoomFactor.Planet) {
                    scale = 0.25; // Small at planet level (most zoomed in)
                } else if (zoom === zoomFactor.Solar) {
                    scale = 0.35; // Slightly larger at solar level
                } else if (zoom === zoomFactor.Local) {
                    scale = 1.5;  // Significantly larger at local level (increased from 0.7)
                } else if (zoom === zoomFactor.Sector) {
                    scale = 2.5;  // Even larger at sector level (increased from 1.3)
                } else if (zoom === zoomFactor.Quadrant) {
                    scale = 3.5;  // Very large at quadrant level (increased from 2.0)
                } else if (zoom === zoomFactor.Max) {
                    scale = 4.5;  // Largest at max level (increased from 3.0)
                } else {
                    // For intermediate zoom levels, calculate scale based on nearest preset zoom levels
                    if (zoom > zoomFactor.Solar && zoom < zoomFactor.Planet) {
                        // Between Solar and Planet
                        const percent = (zoom - zoomFactor.Solar) / (zoomFactor.Planet - zoomFactor.Solar);
                        scale = 0.35 + (0.25 - 0.35) * percent;
                    } else if (zoom > zoomFactor.Local && zoom < zoomFactor.Solar) {
                        // Between Local and Solar
                        const percent = (zoom - zoomFactor.Local) / (zoomFactor.Solar - zoomFactor.Local);
                        scale = 1.5 + (0.35 - 1.5) * percent;
                    } else if (zoom > zoomFactor.Sector && zoom < zoomFactor.Local) {
                        // Between Sector and Local
                        const percent = (zoom - zoomFactor.Sector) / (zoomFactor.Local - zoomFactor.Sector);
                        scale = 2.5 + (1.5 - 2.5) * percent;
                    } else if (zoom > zoomFactor.Quadrant && zoom < zoomFactor.Sector) {
                        // Between Quadrant and Sector
                        const percent = (zoom - zoomFactor.Quadrant) / (zoomFactor.Sector - zoomFactor.Quadrant);
                        scale = 3.5 + (2.5 - 3.5) * percent;
                    } else if (zoom > zoomFactor.Max && zoom < zoomFactor.Quadrant) {
                        // Between Max and Quadrant
                        const percent = (zoom - zoomFactor.Max) / (zoomFactor.Quadrant - zoomFactor.Max);
                        scale = 4.5 + (3.5 - 4.5) * percent;
                    }
                }

                dummy.position.set(x, y, 0.1)  // You can modify the z position if needed
                dummy.scale.set(scale, scale, scale);  // Apply uniform scaling
                dummy.updateMatrix();
                this.planetOwnerInstancedMesh.setMatrixAt(instanceId, dummy.matrix);

                this.planetOwnerInstancedMesh.instanceMatrix.needsUpdate = true;
            }
        },
        findCoordinatesByInstanceId(instanceId: number): { x: number, y: number } {
            if(this.planetOwnerMapCoordinateLookup.has(instanceId)) {
                return this.planetOwnerMapCoordinateLookup.get(instanceId);
            }
            return {x: 0, y: 0}
        }
    }
});